import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Trash2 } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    const r = await api.get("/documents");
    setDocs(r.data);
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await load();
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Removed");
    await load();
  };

  return (
    <div className="max-w-[1100px] mx-auto px-10 py-12" data-testid="documents-page">
      <div className="flex items-center justify-between text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--warm-grey)] border-b border-[var(--ink)]/15 pb-3">
        <span>Workspace · Library</span>
        <span>cosalpha</span>
      </div>

      <div className="mt-12 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="smallcaps text-[var(--saffron)]">No. 04 — Documents</div>
          <h1 className="font-serif text-[48px] font-medium tracking-[-0.02em] mt-4">Source documents.</h1>
          <p className="text-[14px] text-[var(--ink)]/75 mt-3 max-w-[60ch]">Quarterly MIS, CIMs, data rooms, transcripts. Anything you've uploaded for analysis.</p>
        </div>
        <button onClick={() => fileInput.current?.click()} className="inline-flex items-center gap-2 bg-[var(--ink)] hover:bg-[var(--ink)]/90 text-[var(--cream)] px-6 py-3 text-[13px] tracking-wide transition-colors" data-testid="documents-upload-button">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload PDF"}
        </button>
        <input ref={fileInput} type="file" multiple accept="application/pdf" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="documents-file-input" />
      </div>

      <div className="mt-10 stagger">
        {docs.length === 0 && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
            className="bg-white border border-dashed border-[var(--ink)]/30 p-16 text-center"
            data-testid="documents-empty"
          >
            <Upload className="w-6 h-6 mx-auto text-[var(--warm-grey)]" strokeWidth={1.5} />
            <div className="mt-4 font-serif text-[24px]">No documents yet</div>
            <p className="text-[13px] text-[var(--ink)]/70 mt-3">Drop PDFs here, or click upload to get started.</p>
          </div>
        )}
        {docs.map((d, idx) => (
          <div key={d.id} className="grid grid-cols-12 gap-4 py-4 border-b border-[var(--ink)]/10 hover:bg-white items-baseline transition-colors" data-testid={`document-row-${d.id}`}>
            <div className="col-span-1 font-mono text-[10px] text-[var(--saffron)] tabular">{`0${idx + 1}`.slice(-2)}</div>
            <div className="col-span-7 flex items-baseline gap-3 min-w-0">
              <FileText className="w-4 h-4 text-[var(--warm-grey)] shrink-0 self-center" strokeWidth={1.5} />
              <div className="min-w-0">
                <div className="font-serif text-[18px] truncate">{d.filename}</div>
                <div className="text-[11px] text-[var(--warm-grey)] tabular tracking-wide mt-1">
                  {d.pages} pages · {(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString("en-IN")}
                  {!d.has_text && <span className="text-[var(--saffron)]"> · no text extracted</span>}
                </div>
              </div>
            </div>
            <div className="col-span-3 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--warm-grey)]">PDF</div>
            <div className="col-span-1 text-right">
              <button onClick={() => remove(d.id)} className="text-[var(--warm-grey)] hover:text-[#a33] transition-colors" data-testid={`document-delete-${d.id}`}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
